import { useEffect, useState } from "react";
import { shuffleArray } from "./utils/shuffle";
import { ShopTheme } from "./types/types";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import translationEN from "./locales/en/translations.json";
import translationRO from "./locales/ro/translations.json";
import { openVariant, ProductCard } from "./components/ProductCard";

const isProduction = process.env.NODE_ENV === "production";

const resources: Record<string, { translation: Record<string, string> }> = {
  en: {
    translation: translationEN,
  },
  ro: {
    translation: translationRO,
  },
};

i18n.use(initReactI18next).init({
  resources,
  lng: "ro", // default RO
  fallbackLng: "en",
  interpolation: {
    escapeValue: false,
  },
});

const CARD_HEIGHT = 360;

export default function AftermarketWidget({ permanentDomain, sku, country }: { permanentDomain: string; sku: string; country: string }) {
  const [theme, setTheme] = useState<ShopTheme>();
  const [data, setData] = useState<any[]>([]);
  const { t } = useTranslation();

  // Update language when countryCode is available
  useEffect(() => {
    if (country) {
      const language = country.toLowerCase();

      if (resources[language]) {
        i18n.changeLanguage(language);
      } else {
        // Fallback to default language if country code is not supported
        i18n.changeLanguage(i18n.options.fallbackLng as string);
      }
    } else {
      // Fallback to default language if country code is not supported
      i18n.changeLanguage(i18n.options.fallbackLng as string);
    }
  }, [country]);

  const API_URL =
    isProduction || true
      ? `https://partsfinder.clpstudio.ro/api/aftermarkets?permanentDomain=${permanentDomain}&sku=${encodeURIComponent(sku)}&country=${country}`
      : `http://localhost:50483/api/aftermarkets?permanentDomain=${permanentDomain}&sku=${encodeURIComponent(sku)}&country=${country}`;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(API_URL);
        const res = await response.json();

        const theme = res?.theme;
        setTheme(theme);

        const data: any[] = res?.data || [];

        setData(shuffleArray(data));
      } catch (e) {
        console.log(e);
      }
    };

    fetchData();
  }, []);

  if (!theme || !data || data.length === 0) {
    return null;
  }

  return (
    <div
      className="w-full h-full flex flex-col overflow-x-scroll overflow-y-hidden"
      style={{ backgroundColor: theme.aftermarketWidget.bgColor, padding: theme.aftermarketWidget.padding, height: CARD_HEIGHT }}
    >
      <main className="flex flex-col items-center w-full justify-between h-full">
        <div className="flex flex-col w-full h-full">
          <h2 className="lg:text-2xl text-base font-semibold text-gray-900">{t("aftermarket_card_title")}</h2>

          <div className="flex overflow-x-auto overflow-y-hidden h-full w-full pt-1">
            {data?.map((t, index) => {
              const url = t?.image;
              const realtimePrice = t.contextualPricing?.price?.amount ? parseFloat(t.contextualPricing.price.amount).toFixed(2) : "0.00";
              const currencyCode = t.contextualPricing?.price?.currencyCode || "";
              const realtimeCompareAtPrice = t.contextualPricing?.compareAtPrice?.amount;

              return (
                <ProductCard
                  key={index}
                  title={t?.title || ""}
                  url={url || ""}
                  price={realtimePrice + ""}
                  compareAtPrice={realtimeCompareAtPrice}
                  currencyCode={currencyCode}
                  openVariant={() => openVariant(t)}
                  inventoryQuantity={t?.inventoryQuantity}
                  type={t?.productType}
                />
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
}
